import React from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { ViewType } from "../../utils/workspace.types";
import { VIEW_CONFIGURATIONS } from "../../utils/workspace.constants";
import { useDrawingStore } from "../../../../store/useDrawingStore";

interface CameraControllerProps {
  currentView: ViewType;
  projection: "perspective" | "orthographic";
}

/**
 * Camera Controller component - manages camera positioning and view changes
 * Enhanced to properly handle different view modes for accurate selection
 */
export const CameraController: React.FC<CameraControllerProps> = ({
  currentView,
  projection,
}) => {
  const { camera, controls, set, gl, size } = useThree();
  const scale = useDrawingStore((s) => s.viewTransform.scale);
  const setZoomUnified = useDrawingStore((s) => s.setZoomUnified);

  // keep a ref of current scale for wheel handler to avoid stale closures
  const scaleRef = React.useRef<number>(scale || 1);
  React.useEffect(() => {
    scaleRef.current = scale || 1;
  }, [scale]);

  const lastView = React.useRef(currentView);
  const lastProjection = React.useRef(projection);

  // No per-gesture multiplier; unified zoom state is authoritative

  React.useEffect(() => {
    if (!camera || !controls) return;

    // Only reset camera position when view or projection actually changes,
    // NOT when nodes or beams are added/removed
    const viewChanged = currentView !== lastView.current;
    const projectionChanged = projection !== lastProjection.current;

    if (!viewChanged && !projectionChanged) {
      return; // Don't reset camera if only nodes/beams changed
    }

    console.log(
      "🎥 Camera controller updating for view change:",
      currentView,
      "projection:",
      projection
    );

    // Get view configuration
    const viewConfig = VIEW_CONFIGURATIONS[currentView];
    if (!viewConfig) {
      console.warn("Unknown view type:", currentView);
      return;
    }

    // Determine if this should be orthographic view (front/top/side = 2D, isometric = 3D)
    const isOrthographic = currentView !== "isometric";

    // Determine camera UP vector based on view
    // CRITICAL: TOP view needs different UP vector when looking down
    let cameraUp: [number, number, number];
    switch (currentView) {
      case "top":
        // Looking down -Z axis, Y-axis points "up" on screen (North is up)
        cameraUp = [0, 1, 0];
        break;
      case "front":
      case "side":
      case "isometric":
      default:
        // Z-axis points "up" on screen (elevation is up)
        cameraUp = [0, 0, 1];
        break;
    }

    // Only reset camera position on actual view changes
    if (viewChanged || projectionChanged) {
      // Handle camera type switching
      if (
        isOrthographic &&
        projection === "orthographic" &&
        !(camera instanceof THREE.OrthographicCamera)
      ) {
        console.log("🎥 Switching to OrthographicCamera for", currentView);
        // Create orthographic camera with proper frustum
        const aspect = window.innerWidth / window.innerHeight;
        const frustumSize = 50; // Adjust based on your scene size
        const newCamera = new THREE.OrthographicCamera(
          (frustumSize * aspect) / -2,
          (frustumSize * aspect) / 2,
          frustumSize / 2,
          frustumSize / -2,
          0.1,
          1000
        );
        newCamera.position.set(...viewConfig.position);
        newCamera.up.set(...cameraUp); // Set UP vector BEFORE lookAt
        newCamera.lookAt(...viewConfig.target);

        // Use set to replace camera in React Three Fiber
        set({ camera: newCamera });
        console.log("🎥 Switched to OrthographicCamera with UP:", cameraUp);
      } else if (
        !isOrthographic &&
        projection === "perspective" &&
        !(camera instanceof THREE.PerspectiveCamera)
      ) {
        console.log("🎥 Switching to PerspectiveCamera for", currentView);
        // Create perspective camera
        const newCamera = new THREE.PerspectiveCamera(
          45,
          window.innerWidth / window.innerHeight,
          0.1,
          1000
        );
        newCamera.position.set(...viewConfig.position);
        newCamera.up.set(...cameraUp); // Set UP vector BEFORE lookAt
        newCamera.lookAt(...viewConfig.target);

        // Use set to replace camera in React Three Fiber
        set({ camera: newCamera });
        console.log("🎥 Switched to PerspectiveCamera with UP:", cameraUp);
      } else {
        // Same camera type, just reposition
        camera.up.set(...cameraUp); // Set UP vector BEFORE lookAt
        camera.position.set(...viewConfig.position);
        camera.lookAt(...viewConfig.target);
      }

      // Update controls target and sync with camera
      if ((controls as any).target) {
        (controls as any).target.set(...viewConfig.target);
      }

      // CRITICAL FIX: Reset camera UP vector AFTER controls update
      // OrbitControls can corrupt the UP vector during interactions
      camera.up.set(...cameraUp);
      camera.lookAt(...viewConfig.target);
    }

    // Update camera projection matrix
    camera.updateProjectionMatrix();

    // Update controls
    // Configure controls behavior based on view: only isometric allows rotation
    try {
      const ctl = controls as any;
      if (ctl) {
        const isIso = currentView === "isometric";
        // enableRotate controls whether left-drag rotates the camera
        ctl.enableRotate = isIso;
        // enablePan allows panning (we want pan in all orthographic views too)
        ctl.enablePan = true;
        // disable OrbitControls' native wheel zoom - we'll handle wheel/pinch centrally
        ctl.enableZoom = false;

        // For orthographic cameras, we need different zoom behavior
        // DOLLY moves camera closer/farther (3D-style)
        // For 2D orthographic, we want to use the same DOLLY but ensure camera stays orthographic
        if (ctl.mouseButtons) {
          // Map LEFT to ROTATE for all views but control "enableRotate" determines whether it actually rotates.
          // This avoids left-button panning in planar (orthographic) views because enableRotate will be false there.
          ctl.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
          ctl.mouseButtons.RIGHT = THREE.MOUSE.PAN;
          ctl.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY; // Keep as DOLLY but we'll handle orthographic zoom differently
        }

        // For orthographic views, disable camera position changes during zoom
        // and rely on camera.zoom property instead
        if (isOrthographic) {
          // Disable dolly (camera movement) and rely on camera.zoom property
          ctl.zoomToCursor = true; // Ensure zoom-to-cursor semantics are preserved
          ctl.enableDolly = false; // Disable 3D camera movement
        } else {
          // For perspective (isometric), allow dolly but OrbitControls' wheel is disabled
          ctl.zoomToCursor = true;
          ctl.enableDolly = true; // Enable 3D camera movement
        }

        if (ctl.update) ctl.update();

        // CRITICAL FIX: Force camera UP vector AFTER controls update
        // OrbitControls.update() can modify camera.up, causing perspective distortion
        camera.up.set(...cameraUp);
        camera.lookAt(...viewConfig.target);
      }
    } catch (e) {
      console.warn("Failed to apply control flags for view", currentView, e);
    }

    // Update refs
    lastView.current = currentView;
    lastProjection.current = projection;

    console.log("🎥 Camera positioned successfully for", currentView, "view");
  }, [currentView, projection, camera, controls]);

  // Adjust orthographic frustum on canvas resize so content scale doesn't appear to change
  React.useEffect(() => {
    if (!camera) return;
    if (!(camera instanceof THREE.OrthographicCamera)) return;
    const aspect = Math.max(
      0.0001,
      (size?.width || window.innerWidth) /
        Math.max(1, size?.height || window.innerHeight)
    );
    const frustumSize = 50; // world-units height at zoom=1
    camera.left = (-frustumSize * aspect) / 2;
    camera.right = (frustumSize * aspect) / 2;
    camera.top = frustumSize / 2;
    camera.bottom = -frustumSize / 2;
    camera.updateProjectionMatrix();
    (controls as any)?.update?.();
  }, [size?.width, size?.height, camera, controls]);

  // Apply zoom to camera: read from store scale and apply smoothly
  React.useEffect(() => {
    if (!camera) return;
    if (typeof scale !== "number" || isNaN(scale)) return;
    const target = Math.min(4.0, Math.max(0.1, scale || 1));
    const startZoom = (camera as any).zoom ?? 1;
    if (Math.abs(startZoom - target) < 1e-3) return;
    let raf = 0;
    const duration = 120;
    const start = performance.now();
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      const z = startZoom + (target - startZoom) * eased;
      try {
        (camera as any).zoom = z;
        camera.updateProjectionMatrix();
        (controls as any)?.update?.();
      } catch {}
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scale, camera, controls]);

  // Centralized wheel handler: planar views only, zoom-to-cursor and write back to store
  React.useEffect(() => {
    if (!gl || !gl.domElement) return;
    const el = gl.domElement as HTMLElement;

    const onWheel = (e: WheelEvent) => {
      // Prevent default to avoid page scroll and preserve event for R3F
      e.preventDefault();
      e.stopPropagation();

      // Disable in isometric (3D) view
      if (currentView === "isometric") return;

      const deltaY = e.deltaY || 0;
      if (!camera) {
        console.log("❌ No camera available");
        return;
      }

      // Compute pointer NDC
      const rect = el.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );

      // Get world point under cursor using ground plane intersection
      const getWorldPointUnderCursor = (
        ndc: THREE.Vector2
      ): THREE.Vector3 | null => {
        try {
          const raycaster = new THREE.Raycaster();
          raycaster.setFromCamera(ndc, camera);
          // Choose plane based on current view
          const plane =
            currentView === "front"
              ? new THREE.Plane(new THREE.Vector3(0, 0, 1), 0) // Z=0
              : currentView === "top"
                ? new THREE.Plane(new THREE.Vector3(0, 1, 0), 0) // Y=0
                : new THREE.Plane(new THREE.Vector3(1, 0, 0), 0); // X=0
          const intersection = new THREE.Vector3();
          const hit = raycaster.ray.intersectPlane(plane, intersection);
          return hit ? intersection.clone() : null;
        } catch {
          return null;
        }
      };

      // Smooth factor from delta
      const base = Math.max(0.1, scaleRef.current || 1);
      const factor = Math.exp(-deltaY / 240);
      const nextTotal = Math.min(4.0, Math.max(0.1, base * factor));
      const zoomFactor = nextTotal / base;

      // Get world point under cursor before zoom
      const worldPoint = getWorldPointUnderCursor(ndc);

      if (worldPoint && (controls as any)?.target) {
        const target = (controls as any).target;
        const cameraPos = camera.position;
        // Move so world point stays under cursor
        const targetToPoint = new THREE.Vector3().subVectors(
          worldPoint,
          target
        );
        const moveDistance = targetToPoint.length() * (1 - 1 / zoomFactor);
        targetToPoint.normalize().multiplyScalar(moveDistance);

        // Move target and camera towards the cursor point
        target.add(targetToPoint);
        cameraPos.add(targetToPoint);
      }

      // Write back to unified store; smooth camera zoom will follow via scale effect
      setZoomUnified(nextTotal, {
        origin: "cursor",
        clientX: e.clientX,
        clientY: e.clientY,
      });
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [gl, setZoomUnified, currentView, camera, controls]);

  // Listen for disable-camera-controls custom events so other components (like NodeDragHandler)
  // can temporarily disable interactions while the user is performing a drag.
  React.useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent)?.detail;
        const disable = !!detail?.disable;
        const ctl = controls as any;
        if (!ctl) return;
        // Toggle overall control enabled state
        if (typeof ctl.enabled !== "undefined") ctl.enabled = !disable;
        // Re-apply rotate only when not disabled and when current view is isometric
        if (typeof ctl.enableRotate !== "undefined")
          ctl.enableRotate = !disable && currentView === "isometric";
        if (typeof ctl.enablePan !== "undefined") ctl.enablePan = !disable;
        if ((ctl as any).update) (ctl as any).update();
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener(
      "disable-camera-controls",
      handler as EventListener
    );
    return () =>
      window.removeEventListener(
        "disable-camera-controls",
        handler as EventListener
      );
  }, [controls, currentView]);

  return null;
};
